import React, { useState, useEffect, useMemo } from 'react';
import { appConfirmMessage } from '../../../utils/appConfirm';
import {
  FaSearch,
  FaPlus,
  FaExclamationTriangle,
  FaEye,
  FaEdit,
  FaTrash,
  FaHistory,
  FaCheck,
  FaBoxOpen,
  FaClipboardList,
  FaBriefcase,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { useAdminListOverlay } from '../../../utils/useAdminPageSearch';
import '../styles/InventoryProducts.css';
import '../styles/InventoryDashboard.css';
import InventoryUpdate from './InventoryUpdate';
import InventoryView from './InventoryView';
import InventoryHistory from './InventoryHistory';


const FIND_ALL_ITEMS = `
  query FindAllItems {
    findAllItem {
      id
      name
      code
      quantity
      available_quantity
    }
  }
`;

const FIND_ALL_PROJECTS = `
  query FindAllProjects {
    findAllProject {
      id
      name
    }
  }
`;

const FIND_ALL_INVENTORY_MANAGEMENT = `
  query FindAllInventoryManagement {
    findAllInventoryManagement {
      id
      tool {
        id
        name
        code
        quantity
        available_quantity
      }
      project {
        id
        name
      }
      quantityAssigned
      assignedAt
      assignedBy
      remark
      updateAction
      category
      createdAt
      updatedAt
    }
  }
`;

const REMOVE_ALL_INVENTORY_MANAGEMENT = `
  mutation RemoveAllInventoryManagement {
    removeAllInventoryManagement
  }
`;

const REMOVE_ALL_ITEMS = `
  mutation RemoveAllItems {
    removeAllItems
  }
`;

const REMOVE_INVENTORY_MANAGEMENT = `
  mutation RemoveInventoryManagement($id: String!) {
    removeInventoryManagement(id: $id) {
      id
    }
  }
`;

const InventoryDashboard = () => {
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [inventoryData, setInventoryData] = useState([]);
  const [inventoryMgmtData, setInventoryMgmtData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const overlay = useAdminListOverlay(inventoryData);

  // Fetch inventory data - show ALL Items in the table (matching the low stock alerts)
  const fetchInventoryData = async () => {
    try {
      // Items are fetched in the parent loader (`fetchData`) and stored in state.
      // Fetch inventory management records to get project assignments
      let inventoryMgmtData = [];
      try {
        const mgmtData = await graphqlRequest(FIND_ALL_INVENTORY_MANAGEMENT);
        inventoryMgmtData = mgmtData?.findAllInventoryManagement || [];
        setInventoryMgmtData(inventoryMgmtData);
      } catch (mgmtError) {
        console.warn('Could not fetch inventory management records:', mgmtError);
        setInventoryMgmtData([]);
        // Don't return - we can still show items without project info
      }

      // Create a map of itemId -> latest inventory management record
      const itemToLatestMgmt = {};
      inventoryMgmtData.forEach(record => {
        const itemId = record.tool?.id;
        if (itemId) {
          if (!itemToLatestMgmt[itemId] || 
              new Date(record.assignedAt || record.createdAt) > 
              new Date(itemToLatestMgmt[itemId].assignedAt || itemToLatestMgmt[itemId].createdAt)) {
            itemToLatestMgmt[itemId] = record;
          }
        }
      });
      
      // Transform inventory management records to table format
      // Show each inventory management record as a separate row
      const transformedItems = inventoryMgmtData.map((record, index) => {
        const item = record.tool;
        return {
          id: record.id || `record-${index}`, // Use inventory management record ID
          inventoryManagementId: record.id, // Store inventory management record ID for deletion
          item: item?.name || 'Unknown',
          code: item?.code || 'N/A',
          quantity: `${record.quantityAssigned || 0} pcs`, // Show the assigned quantity from the record
          quantityValue: record.quantityAssigned || 0,
          project: record.project?.name || 'No project',
          category: record.category || '—',
          updateAction: record.updateAction || 'Add to Stock',
          remark: record.remark || (record.assignedBy ? `Assigned by ${record.assignedBy}` : 'Available'),
          date: record.assignedAt 
            ? new Date(record.assignedAt).toISOString().split('T')[0]
            : (record.createdAt 
                ? new Date(record.createdAt).toISOString().split('T')[0]
                : new Date().toISOString().split('T')[0]),
          itemId: item?.id,
          projectId: record.project?.id,
          assignedAt: record.assignedAt,
          assignedBy: record.assignedBy,
          totalQuantity: item?.quantity,
          availableQuantity: item?.available_quantity,
          fromBackend: true,
          completed: false,
        };
      });
      
      setInventoryData(transformedItems);
    } catch (error) {
      console.error('❌ Error fetching inventory data:', error);
      toast.error(`Failed to load inventory data: ${error.message}`);
    }
  };

  // Fetch items, projects, and inventory updates from backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [itemsResult, projectsResult] = await Promise.allSettled([
          graphqlRequest(FIND_ALL_ITEMS),
          graphqlRequest(FIND_ALL_PROJECTS),
        ]);

        if (itemsResult.status === 'fulfilled') {
          setItems(itemsResult.value?.findAllItem || []);
        } else {
          console.error('Error fetching items:', itemsResult.reason);
          toast.error(`Failed to load items: ${itemsResult.reason?.message || itemsResult.reason}`);
        }
                                               
        if (projectsResult.status === 'fulfilled') {
          setProjects(projectsResult.value?.findAllProject || []);
        } else {
          console.error('Error fetching projects:', projectsResult.reason);
          toast.error(
            `Failed to load projects: ${projectsResult.reason?.message || projectsResult.reason}`,
          );
        }

        await fetchInventoryData();
      } catch (error) {
        console.error(' Error fetching data:', error);
        toast.error(`Failed to load data: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Low stock: remaining on-hand after Issued / Used / etc.
  const stockAlerts = useMemo(() => {
    const LOW_STOCK_MAX = 3;
    const byId = new Map();

    for (const item of items) {
      if (item?.id) byId.set(item.id, item);
    }

    for (const record of inventoryMgmtData) {
      const tool = record?.tool;
      if (!tool?.id) continue;
      const existing = byId.get(tool.id);
      if (!existing) {
        byId.set(tool.id, tool);
        continue;
      }
      byId.set(tool.id, {
        ...tool,
        ...existing,
        available_quantity:
          existing.available_quantity ?? tool.available_quantity,
        quantity: existing.quantity ?? tool.quantity,
        name: existing.name || tool.name,
      });
    }

    const lowStockItems = [];
    for (const item of byId.values()) {
      const listed = Number(item.quantity);
      const availableRaw = item.available_quantity;
      const onHand =
        availableRaw === null || availableRaw === undefined
          ? Number.isFinite(listed)
            ? listed
            : NaN
          : Math.max(0, Number(availableRaw) || 0);
      if (!Number.isFinite(onHand)) continue;
      // Include 0 (out of stock) and 1–3 (low stock).
      if (onHand >= 0 && onHand <= LOW_STOCK_MAX) {
        lowStockItems.push({ ...item, _alertQty: onHand });
      }
    }

    const uniqueItems = lowStockItems.reduce((acc, item) => {
      const itemName = item.name?.toLowerCase() || item.id;
      const availableQty = item._alertQty;
      const existingQty = acc[itemName] ? acc[itemName]._alertQty : Infinity;
      if (!acc[itemName] || availableQty < existingQty) {
        acc[itemName] = item;
      }
      return acc;
    }, {});

    return Object.values(uniqueItems)
      .sort((a, b) => a._alertQty - b._alertQty)
      .map((item) =>
        item._alertQty === 0
          ? `${item.name}: Out of stock (0 pcs left).`
          : `${item.name}: Only ${item._alertQty} pcs left.`,
      );
  }, [items, inventoryMgmtData]);

  // Handle new inventory update - refresh from backend (toast is shown by the form)
  const handleInventoryAdded = async () => {
    try {
      const itemsData = await graphqlRequest(FIND_ALL_ITEMS);
      const fetchedItems = itemsData?.findAllItem || [];
      setItems(fetchedItems);
    } catch (error) {
      console.error('Error refreshing items:', error);
    }

    await fetchInventoryData();
  };

  // Handle inventory update - refresh from backend (toast is shown by the form)
  const handleInventoryUpdated = async () => {
    try {
      const itemsData = await graphqlRequest(FIND_ALL_ITEMS);
      const fetchedItems = itemsData?.findAllItem || [];
      setItems(fetchedItems);
    } catch (error) {
      console.error('Error refreshing items:', error);
    }

    await fetchInventoryData();
  };

  // Mark a row as completed (UI only)
  const handleMarkComplete = (rowId) => {
    setInventoryData((prev) =>
      prev.map((row) =>
        row.id === rowId ? { ...row, completed: !row.completed } : row
      )
    );
  };

  // Handle delete all - delete ALL inventory data (items, item history, and inventory management)
  const handleDeleteAll = async () => {
    if (await appConfirmMessage('⚠️ WARNING: This will delete EVERYTHING - all items, item history, and inventory update history. This action cannot be undone. Are you absolutely sure?')) {
      if (await appConfirmMessage('This is your last chance. Click OK to permanently delete ALL inventory data and start fresh.')) {
        try {
          // Delete all inventory management records first
          await graphqlRequest(REMOVE_ALL_INVENTORY_MANAGEMENT);
          await graphqlRequest(REMOVE_ALL_ITEMS);

          toast.success('All inventory data deleted successfully! You can now start fresh.');
          setItems([]);
          setInventoryMgmtData([]);
          setInventoryData([]);
          await fetchInventoryData();
        } catch (error) {
          console.error('Error deleting all data:', error);
          toast.error(`Failed to delete all data: ${error.message}`);
        }
      }
    }
  };

  // Delete a single inventory update history row (restores stock if it was an issue)
  const handleDeleteOne = async (row) => {
    const id = row?.inventoryManagementId || row?.id;
    if (!id || String(id).startsWith('record-')) {
      toast.error('Cannot delete this row.');
      return;
    }
    if (
      !await appConfirmMessage(
        `Delete this inventory update for "${row.item || 'item'}" (${row.quantity || ''})?`,
      )
    ) {
      return;
    }
    try {
      await graphqlRequest(REMOVE_INVENTORY_MANAGEMENT, { id });
      toast.success('Inventory update deleted.');
      const itemsData = await graphqlRequest(FIND_ALL_ITEMS);
      setItems(itemsData?.findAllItem || []);
      await fetchInventoryData();
    } catch (error) {
      console.error('Error deleting inventory update:', error);
      toast.error(`Failed to delete: ${error.message}`);
    }
  };

  // History "Quantity" is how many were issued in that update — not warehouse stock.
  // Keep a neutral style so it is not confused with the low-stock alert.

  // Filter inventory data based on search term
  const filteredInventoryData = useMemo(() => {
    if (!searchTerm) return inventoryData;
    
    const term = searchTerm.toLowerCase();
    return inventoryData.filter(item =>
      item.item?.toLowerCase().includes(term) ||
      item.code?.toLowerCase().includes(term) ||
      item.project?.toLowerCase().includes(term) ||
      item.category?.toLowerCase().includes(term) ||
      item.remark?.toLowerCase().includes(term)
    );
  }, [inventoryData, searchTerm]);

  const stats = useMemo(() => {
    const projects = new Set(
      inventoryData.map((row) => row.project).filter((p) => p && p !== 'No project'),
    );
    return {
      updates: inventoryData.length,
      projects: projects.size,
      items: items.length,
      low: stockAlerts.length,
    };
  }, [inventoryData, items.length, stockAlerts.length]);

  return (
    <div className="ip-page">
      <header className="ip-header">
        <div>
          <h1>Inventory Updates</h1>
          <p className="ip-sub">
            Stock issued, used, or assigned to jobs. Product catalogue lives under
            Inventory → Products.
          </p>
        </div>
        <div className="ip-header-actions">
          <button
            type="button"
            className="ip-add ip-add--ghost"
            onClick={() => overlay.patch({ action: 'history' }, ['view', 'edit'])}
          >
            <FaHistory /> History
          </button>
          <button
            type="button"
            className="ip-add ip-add--ghost ip-add--danger"
            onClick={handleDeleteAll}
          >
            <FaTrash /> Delete all
          </button>
          <button
            type="button"
            className="ip-add"
            onClick={() => overlay.openAdd()}
          >
            <FaPlus /> Add Update
          </button>
        </div>
      </header>

      <section className="ip-stats">
        <article className="ip-stat">
          <div className="ip-stat-icon ip-stat-icon--purple">
            <FaClipboardList />
          </div>
          <div>
            <div className="ip-stat-value">{stats.updates}</div>
            <div className="ip-stat-label">Updates</div>
          </div>
        </article>
        <article className="ip-stat">
          <div className="ip-stat-icon ip-stat-icon--green">
            <FaBoxOpen />
          </div>
          <div>
            <div className="ip-stat-value">{stats.items}</div>
            <div className="ip-stat-label">Products</div>
          </div>
        </article>
        <article className="ip-stat">
          <div className="ip-stat-icon ip-stat-icon--orange">
            <FaExclamationTriangle />
          </div>
          <div>
            <div className="ip-stat-value">{stats.low}</div>
            <div className="ip-stat-label">Low stock</div>
          </div>
        </article>
        <article className="ip-stat">
          <div className="ip-stat-icon ip-stat-icon--red">
            <FaBriefcase />
          </div>
          <div>
            <div className="ip-stat-value">{stats.projects}</div>
            <div className="ip-stat-label">Jobs with stock</div>
          </div>
        </article>
      </section>

      {stockAlerts.length > 0 && (
        <div className="iv-alert">
          <div className="iv-alert-head">
            <FaExclamationTriangle />
            Low stock alert
          </div>
          <ul>
            {stockAlerts.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="ip-panel">
        <div className="ip-filters">
          <div className="ip-search">
            <FaSearch />
            <input
              type="text"
              placeholder="Search item, project, or action..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="ip-table-wrap">
          <table className="ip-table">
            <thead>
              <tr>
                <th>Project</th>
                <th>Category</th>
                <th>Item</th>
                <th>Code</th>
                <th>Quantity</th>
                <th>Action</th>
                <th>Date</th>
                <th>Remark</th>
                <th> </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="iv-empty">
                    Loading updates…
                  </td>
                </tr>
              ) : filteredInventoryData.length === 0 ? (
                <tr>
                  <td colSpan="9" className="iv-empty">
                    {searchTerm
                      ? 'No updates match your search.'
                      : 'No inventory updates yet. Click Add Update to record one.'}
                  </td>
                </tr>
              ) : (
                filteredInventoryData.map((row) => (
                  <tr
                    key={row.id}
                    className={row.completed ? 'iv-row-done' : undefined}
                  >
                    <td>{row.project || '—'}</td>
                    <td>{row.category || '—'}</td>
                    <td className="ip-name">{row.item}</td>
                    <td className="ip-sku">{row.code}</td>
                    <td>{row.quantity}</td>
                    <td>{row.updateAction || 'Add to Stock'}</td>
                    <td>{row.date}</td>
                    <td>{row.remark || '—'}</td>
                    <td>
                      <div className="ip-actions">
                        <button
                          type="button"
                          className="ip-icon-btn"
                          title="View"
                          onClick={() => overlay.openView(row)}
                        >
                          <FaEye />
                        </button>
                        <button
                          type="button"
                          className="ip-icon-btn"
                          title="Edit"
                          onClick={() => overlay.openEdit(row)}
                        >
                          <FaEdit />
                        </button>
                        {row.inventoryManagementId && (
                          <button
                            type="button"
                            className="ip-icon-btn"
                            title={
                              row.completed
                                ? 'Mark as incomplete'
                                : 'Mark as complete'
                            }
                            onClick={() => handleMarkComplete(row.id)}
                          >
                            <FaCheck
                              style={{
                                color: row.completed ? '#16a34a' : undefined,
                              }}
                            />
                          </button>
                        )}
                        {row.inventoryManagementId && (
                          <button
                            type="button"
                            className="ip-icon-btn danger"
                            title="Delete this update"
                            onClick={() => void handleDeleteOne(row)}
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {overlay.showForm && (
        <InventoryUpdate
          onClose={overlay.closeOverlay}
          onInventoryAdded={overlay.editing ? handleInventoryUpdated : handleInventoryAdded}
          items={items}
          projects={projects}
          editData={overlay.editing}
        />
      )}

      {overlay.viewing && (
        <InventoryView
          data={overlay.viewing}
          onClose={overlay.closeOverlay}
        />
      )}

      {overlay.action === 'history' && (
        <InventoryHistory
          data={inventoryData}
          onClose={overlay.closeOverlay}
        />
      )}
    </div>
  );
};

export default InventoryDashboard;
